/**
 * URLs de archivos protegidos: imágenes, miniaturas y archivos DICOM.
 *
 * POR QUÉ EXISTE
 * ==============
 * Las etiquetas `<img src>` y el cargador de Cornerstone descargan por su
 * cuenta y no pasan por el cliente HTTP de la aplicación, así que no pueden
 * añadir la cabecera `Authorization`. La credencial viaja entonces en la
 * propia dirección, que es lo que el backend acepta en estas rutas.
 *
 * Construirla a mano en cada pantalla es lo que llevó a que el panel de
 * miniaturas leyera por dentro la credencial del PERSONAL y por eso no
 * sirviera para el portal del paciente, que usa otra. Aquí se decide una vez
 * y cada portal pide la suya.
 *
 * La credencial acaba en el historial del navegador y en los registros del
 * servidor intermedio, y por eso las de estas rutas son cortas y solo dan
 * acceso a lo que ese usuario ya podía ver.
 */

import { tokenStorage, patientTokenStorage } from './api'

/** Quién mira: el personal de la IPS o el paciente en su portal. */
export type Portal = 'personal' | 'paciente'

const credencialDe = (portal: Portal): string | null =>
  portal === 'paciente'
    ? patientTokenStorage.getAccessToken()
    : tokenStorage.getAccessToken()

/**
 * Añade la credencial del portal a una dirección ya construida.
 *
 * Devuelve `null` si no hay sesión: es preferible no pintar nada a pedir el
 * archivo sin credencial y recibir un 401 que el usuario vería como una
 * imagen rota sin explicación.
 */
export const conCredencial = (url: string, portal: Portal): string | null => {
  const credencial = credencialDe(portal)
  if (!credencial) return null

  const separador = url.includes('?') ? '&' : '?'
  return `${url}${separador}token=${encodeURIComponent(credencial)}`
}

/**
 * Prepara las direcciones de un conjunto de imágenes para el visor.
 *
 * Se descartan las que no se pueden firmar en lugar de dejar huecos: el visor
 * numera "imagen 3 de 24" por la posición en la lista, y una entrada vacía
 * correría la numeración.
 */
export const urlsParaVisor = (
  urls: readonly (string | null | undefined)[],
  portal: Portal
): string[] =>
  urls
    .map((url) => (url ? conCredencial(url, portal) : null))
    .filter((url): url is string => url !== null)
